import {
  BIO_UNIT_CHARACTERS,
  BIO_UNIT_WORDS,
  RESUME_BIO_UNIT,
  RESUME_BIO_LIMITS,
  DEFAULT_RESUME_BIO_LIMIT,
  RESUME_SHARED_BUDGETS,
  DEFAULT_RESUME_SHARED_BUDGET,
  RESUME_SHARED_BUDGET_FIELDS,
} from "constants/options";

/**
 * ---------------------------------------------------------------------------
 * Bio limit — the one place the cut is decided
 * ---------------------------------------------------------------------------
 * The editor shades what falls past the limit, the live preview drops it, and
 * the exported PDF drops it. All three call in here, so they cannot disagree
 * about where the cut falls.
 */

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

/** Elements that carry meaning with no text of their own. */
const VOID_ELEMENTS = /^(BR|IMG|HR|INPUT|VIDEO|IFRAME|SOURCE|EMBED)$/;
const VOID_SELECTOR = "img,br,hr,input,video,iframe,embed,source";

/** Class the editor wraps overflow text in. Never persisted, never printed. */
export const BIO_OVERFLOW_CLASS = "ql-bio-overflow";

/** Marks the first overflow run, so the cut line is drawn once. */
export const FIRST_RUN_CLASS = "bio-overflow-first";

/**
 * Split a string into grapheme clusters, so a family emoji or a flag counts as
 * one character and never gets cut in half. Falls back to code points where
 * Intl.Segmenter is missing, which still keeps surrogate pairs intact.
 */
export function segmentGraphemes(text = "") {
  if (!text) return [];
  if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
    try {
      const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
      return Array.from(segmenter.segment(text), (s) => s.segment);
    } catch (e) {
      // fall through to code points
    }
  }
  return Array.from(text);
}

export function countGraphemes(text = "") {
  return segmentGraphemes(text).length;
}

export function countWords(text = "") {
  const matches = String(text).match(/\S+/g);
  return matches ? matches.length : 0;
}

/** How many units of `unit` the text uses. */
export function countUnits(text = "", unit = RESUME_BIO_UNIT) {
  return unit === BIO_UNIT_WORDS ? countWords(text) : countGraphemes(text);
}

function splitCharacters(text, limit) {
  const graphemes = segmentGraphemes(text);
  if (graphemes.length <= limit) return { kept: text, overflow: "" };
  const kept = graphemes.slice(0, limit).join("");
  return { kept, overflow: text.slice(kept.length) };
}

function splitWords(text, limit) {
  const word = /\S+/g;
  let match;
  let seen = 0;
  while ((match = word.exec(text)) !== null) {
    seen += 1;
    if (seen > limit) {
      // Cut at the end of the last allowed word: the whitespace ahead of this
      // one belongs to the in-limit segment, not the overflow.
      return { kept: text.slice(0, match.index), overflow: text.slice(match.index) };
    }
  }
  return { kept: text, overflow: "" };
}

/**
 * Split plain text at `limit` units.
 *
 * @param {string} text
 * @param {number} limit
 * @param {'characters'|'words'} unit
 * @returns {{kept: string, overflow: string}}
 */
export function splitByLimit(text = "", limit = 0, unit = RESUME_BIO_UNIT) {
  const source = text == null ? "" : String(text);
  const max = Number(limit);
  if (!source) return { kept: "", overflow: "" };
  if (!Number.isFinite(max) || max <= 0) return { kept: "", overflow: source };
  return unit === BIO_UNIT_WORDS
    ? splitWords(source, max)
    : splitCharacters(source, max);
}

/** Unwrap the editor's overflow spans, leaving their text in place. */
export function stripOverflowMarkup(html = "") {
  if (!html || html.indexOf(BIO_OVERFLOW_CLASS) === -1) return html || "";
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("." + BIO_OVERFLOW_CLASS).forEach((span) => {
    span.replaceWith(...span.childNodes);
  });
  container.normalize();
  return container.innerHTML;
}

/** The text a reader actually sees, with tags removed. */
export function htmlToVisibleText(html = "") {
  if (!html) return "";
  const container = document.createElement("div");
  container.innerHTML = html;
  return container.textContent || "";
}

function pruneEmpty(root) {
  const visit = (el) => {
    Array.from(el.children).forEach(visit);
    if (VOID_ELEMENTS.test(el.tagName)) return;
    if (el.textContent.trim()) return;
    if (el.querySelector(VOID_SELECTOR)) return;
    el.remove();
  };
  Array.from(root.children).forEach(visit);
}

/** Keep only the visible graphemes in [from, to), preserving markup. */
function sliceHtml(html, from, to) {
  const container = document.createElement("div");
  container.innerHTML = html;

  let seen = 0;
  const walk = (node) => {
    if (node.nodeType === TEXT_NODE) {
      const graphemes = segmentGraphemes(node.textContent);
      const start = seen;
      const end = seen + graphemes.length;
      seen = end;
      const lo = Math.max(from, start);
      const hi = Math.min(to, end);
      node.textContent = hi <= lo ? "" : graphemes.slice(lo - start, hi - start).join("");
      return;
    }
    if (node.nodeType === ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(walk);
    }
  };

  Array.from(container.childNodes).forEach(walk);
  pruneEmpty(container);
  return container.innerHTML;
}

/**
 * Split rich-text HTML at `limit` units of visible text, keeping markup intact
 * on both sides. Any existing overflow markup is stripped first, so re-running
 * this over already-shaded HTML is safe.
 *
 * @returns {{keptHtml: string, overflowHtml: string}}
 */
export function splitHtmlByLimit(html = "", limit = 0, unit = RESUME_BIO_UNIT) {
  const clean = stripOverflowMarkup(html);
  if (!clean) return { keptHtml: "", overflowHtml: "" };

  const text = htmlToVisibleText(clean);
  const { kept } = splitByLimit(text, limit, unit);
  const cut = countGraphemes(kept);

  if (cut >= countGraphemes(text)) return { keptHtml: clean, overflowHtml: "" };
  if (cut === 0) return { keptHtml: "", overflowHtml: clean };

  return {
    keptHtml: sliceHtml(clean, 0, cut),
    overflowHtml: sliceHtml(clean, cut, Infinity),
  };
}

/**
 * Wrap the past-the-limit part of `html` in place, leaving one continuous
 * document. Unlike splitHtmlByLimit, which returns two independent trees, this
 * keeps a paragraph that straddles the boundary as a single paragraph — so a
 * mirror rendering the result wraps its lines exactly like the original.
 *
 * Used only by the editor's shading overlay. Never persisted, never printed.
 */
export function markOverflowHtml(html = "", limit = 0, unit = RESUME_BIO_UNIT) {
  const clean = stripOverflowMarkup(html);
  if (!clean) return "";

  const container = document.createElement("div");
  container.innerHTML = clean;

  const text = container.textContent || "";
  const { kept } = splitByLimit(text, limit, unit);
  const cut = countGraphemes(kept);
  if (cut >= countGraphemes(text)) return clean;

  let seen = 0;
  let isFirst = true;

  const wrap = (tail, marker) => {
    const span = document.createElement("span");
    span.className = marker ? BIO_OVERFLOW_CLASS + " " + FIRST_RUN_CLASS : BIO_OVERFLOW_CLASS;
    span.textContent = tail;
    return span;
  };

  const walk = (node) => {
    if (node.nodeType === TEXT_NODE) {
      const graphemes = segmentGraphemes(node.textContent);
      const start = seen;
      const end = seen + graphemes.length;
      seen = end;
      if (end <= cut) return;
      if (start >= cut) {
        node.replaceWith(wrap(node.textContent, isFirst));
      } else {
        const head = document.createTextNode(graphemes.slice(0, cut - start).join(""));
        node.replaceWith(head, wrap(graphemes.slice(cut - start).join(""), isFirst));
      }
      isFirst = false;
      return;
    }
    if (node.nodeType === ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(walk);
    }
  };

  Array.from(container.childNodes).forEach(walk);
  return container.innerHTML;
}

/** The bio limit for a template. Never hardcode this at a call site. */
export function getBioLimit(templateName) {
  const limit = RESUME_BIO_LIMITS[templateName];
  return typeof limit === "number" ? limit : DEFAULT_RESUME_BIO_LIMIT;
}

/**
 * The bio as a given template will actually print it. Every render surface
 * calls this, so the live preview and the PDF cut at the same place.
 *
 * Bio is the first field in the shared pool (MCF-637), so it may use the whole
 * template budget — the Professional fields take what's left (handled upstream
 * by applySharedBudget). Cutting bio at the shared budget keeps both halves of
 * the pool consistent with one number per template.
 */
export function truncateBioForTemplate(html, templateName) {
  return splitHtmlByLimit(html || "", getSharedBudget(templateName)).keptHtml;
}

/** Counter formatting, per the design: "1,591 / 215". */
export function formatBioCount(n) {
  return Number(n || 0).toLocaleString("en-AU");
}

/* ===========================================================================
 * Shared character budget (MCF-637)
 * ===========================================================================
 * A template's budget is shared across its resume text boxes — the Bio plus the
 * six Professional Information fields — in the order given by
 * RESUME_SHARED_BUDGET_FIELDS. Earlier fields keep their text; once the pool is
 * used up, later fields are cut from the preview/PDF first. The one counter each
 * box shows is the shared remaining (budget − everything typed across the pool).
 *
 * Bio is rich-text HTML; the six Professional fields are plain text. Both are
 * measured in visible grapheme clusters, so the counts are comparable.
 * ======================================================================== */

/** The shared budget for a template. Never hardcode this at a call site. */
export function getSharedBudget(templateName) {
  const n = RESUME_SHARED_BUDGETS[templateName];
  return typeof n === "number" ? n : DEFAULT_RESUME_SHARED_BUDGET;
}

/** Visible-text length of one field value (bio is HTML, the rest are plain). */
function fieldUnits(key, value, unit) {
  if (value == null) return 0;
  const text = key === "bio" ? htmlToVisibleText(String(value)) : String(value);
  return countUnits(text, unit);
}

/**
 * How the shared pool is currently spent for a set of field values.
 *
 * @param {object} values  map of field key -> value (bio HTML, others plain)
 * @returns {{budget:number, totalUsed:number, remaining:number,
 *            perField:Object<string,{used:number, effectiveLimit:number,
 *            kept:number, overflow:number}>}}
 *   remaining is budget − totalUsed and may be negative (over budget).
 *   effectiveLimit is what this field may keep after the fields ahead of it in
 *   drain order have taken their share; kept/overflow split this field's own
 *   text against that limit.
 */
export function computeSharedUsage(values = {}, templateName, unit = RESUME_BIO_UNIT) {
  const budget = getSharedBudget(templateName);
  let consumed = 0;
  let totalUsed = 0;
  const perField = {};

  RESUME_SHARED_BUDGET_FIELDS.forEach((key) => {
    const used = fieldUnits(key, values[key], unit);
    totalUsed += used;
    const effectiveLimit = Math.max(0, budget - consumed);
    const kept = Math.min(used, effectiveLimit);
    consumed += kept;
    perField[key] = { used, effectiveLimit, kept, overflow: Math.max(0, used - kept) };
  });

  return { budget, totalUsed, remaining: budget - totalUsed, perField };
}

/**
 * Return a copy of `moreInfo` with every pooled field cut to what the template
 * will actually print. Bio keeps its markup (splitHtmlByLimit); the plain
 * Professional fields are sliced on grapheme boundaries. Call this once, where
 * the render reads its data, so the live preview and the PDF cut identically.
 */
export function applySharedBudget(moreInfo = {}, templateName, unit = RESUME_BIO_UNIT) {
  if (!moreInfo) return moreInfo;
  const { perField } = computeSharedUsage(moreInfo, templateName, unit);
  const out = { ...moreInfo };

  RESUME_SHARED_BUDGET_FIELDS.forEach((key) => {
    const limit = perField[key]?.effectiveLimit ?? 0;
    const value = moreInfo[key];
    if (value == null || value === "") return;
    if (key === "bio") {
      out.bio = splitHtmlByLimit(String(value), limit, unit).keptHtml;
    } else {
      const { kept } = splitByLimit(String(value), limit, unit);
      out[key] = kept;
    }
  });

  return out;
}

export { BIO_UNIT_CHARACTERS, BIO_UNIT_WORDS };
